package kz.dentalcrm.app.screens.diagnosis.edit

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.BottomSheetDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Text
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import compose.icons.FeatherIcons
import compose.icons.feathericons.FilePlus
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.delete
import dentalcrm.composeapp.generated.resources.diagnosis_edit_title
import dentalcrm.composeapp.generated.resources.edit
import dentalcrm.composeapp.generated.resources.service_naming
import kz.dentalcrm.app.core.ui.UiStateScaffold
import kz.dentalcrm.app.core.ui.components.UIButton
import kz.dentalcrm.app.core.ui.components.UITextInput
import kz.dentalcrm.app.data.network.response_model.diagnosis.Diagnosis
import kz.dentalcrm.app.screens.diagnosis.edit.mvi.EditDiagnosisViewEvent
import kz.dentalcrm.app.screens.diagnosis.edit.mvi.EditDiagnosisViewState
import kz.dentalcrm.app.theme.DefaultTheme
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun EditDiagnosisDialog(
    diagnosis: Diagnosis,
    onEdit: () -> Unit,
    onDelete: (Int) -> Unit,
    onDismiss: () -> Unit,
) {
    val modalBottomSheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    val screenModel = koinViewModel<EditDiagnosisViewModel>()
    val state by screenModel.viewState.collectAsState()

    LaunchedEffect(state.screenState) {
        when (state.screenState) {
            is EditDiagnosisViewState.ScreenState.Edited -> {
                screenModel.handleEvents(EditDiagnosisViewEvent.Reset)
                onEdit()
                onDismiss()
            }
            is EditDiagnosisViewState.ScreenState.Deleted -> {
                screenModel.handleEvents(EditDiagnosisViewEvent.Reset)
                onDelete(diagnosis.id)
                onDismiss()
            }
            else -> Unit
        }
    }

    ModalBottomSheet(
        modifier = Modifier
            .statusBarsPadding()
            .imePadding(),
        onDismissRequest = {
            screenModel.handleEvents(EditDiagnosisViewEvent.Reset)
            onDismiss()
        },
        sheetState = modalBottomSheetState,
        dragHandle = { BottomSheetDefaults.DragHandle() },
        containerColor = DefaultTheme.colors.surfaceSecondary,
        content = {
            UiStateScaffold(
                isLoading = state.screenState is EditDiagnosisViewState.ScreenState.Loading,
                modifier = Modifier.background(Color.Transparent),
                content = {
                    AddServiceContent(
                        diagnosis = diagnosis,
                        state = state,
                        event = screenModel::handleEvents
                    )
                }
            )
        }
    )
}

@Composable
private fun AddServiceContent(
    diagnosis: Diagnosis,
    state: EditDiagnosisViewState,
    event: (EditDiagnosisViewEvent) -> Unit,
) {
    Column(
        modifier = Modifier
            .background(
                color = DefaultTheme.colors.surfaceSecondary,
            )
            .fillMaxWidth(),
    ) {
        var name by remember { mutableStateOf("") }

        Text(
            modifier = Modifier.padding(horizontal = 16.dp),
            text = stringResource(Res.string.diagnosis_edit_title),
            style = DefaultTheme.typography.bodyLargeMedium,
            color = DefaultTheme.colors.onSurface
        )
        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        HorizontalDivider(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
            thickness = 1.dp,
            color = DefaultTheme.colors.onSurfaceVariant.copy(
                alpha = 0.2f
            )
        )
        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        UITextInput(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .fillMaxWidth(),
            defaultValue = diagnosis.name ?: "",
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Text
            ),
            singleLine = false,
            leadingIcon = FeatherIcons.FilePlus,
            label = stringResource(Res.string.service_naming),
        ) {
            name = it
        }
        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        Row(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .fillMaxWidth(),
            horizontalArrangement = Arrangement.Center
        ) {
            UIButton(
                modifier = Modifier.weight(1f),
                containerColor = DefaultTheme.colors.error,
                text = stringResource(Res.string.delete),
                isLoading = state.screenState is EditDiagnosisViewState.ScreenState.Editing,
                onClick = {
                    event(
                        EditDiagnosisViewEvent.DeleteDiagnosis(
                            diagnosis = diagnosis
                        )
                    )
                },
            )
            Spacer(modifier = Modifier.width(8.dp))
            UIButton(
                modifier = Modifier.weight(1f),
                enabled = name.isNotEmpty(),
                text = stringResource(Res.string.edit),
                isLoading = state.screenState is EditDiagnosisViewState.ScreenState.Editing,
                onClick = {
                    event(
                        EditDiagnosisViewEvent.EditDiagnosis(
                            diagnosis = diagnosis,
                            naming = name
                        )
                    )
                },
            )
        }

        Spacer(
            modifier = Modifier.fillMaxWidth().height(32.dp)
                .navigationBarsPadding()
        )
    }
}